import { BASE_URL, ENV } from './config';
import { dispatch } from './redux/store';
import { doneLoading, startLoading } from './redux/slices/templateRedux/productManagementRedux/loadingRedux';
import Compressor from 'compressorjs';
import { toast } from 'react-toastify';

const MAX_TOAST_LIFETIME = 750;

const withToast = async (promise, { pending = 'Loading...', stopAutoClose } = {}) => {
  
  const id = toast.promise(promise, {
    pending: { render: pending, autoClose: MAX_TOAST_LIFETIME },
    success: { render: ({ data }) => data?.message || 'Success', autoClose: 750 },
    error: { render: ({ data }) => data?.message || 'An error occurred', autoClose: stopAutoClose ? false : 750 },
  });
  const killer = setTimeout(() => toast.dismiss(id), MAX_TOAST_LIFETIME);
  try {
    const result = await promise;
    clearTimeout(killer);
    return result;
  } catch (e) {
    clearTimeout(killer);
    throw e;
  }
};

const runRequest = async (makeRequest, { usePromiseToast = true, pendingText = 'Loading...', stopAutoClose = false, silentErrors = false } = {}) => {
  dispatch(startLoading());
  try {
    const exec = async () => {
      const res = await makeRequest();
      const data = await res.json();
      if (!data?.status) throw data;
      return data;
    };
    return usePromiseToast ? await withToast(exec(), { pending: pendingText, stopAutoClose }) : await exec();
  } catch (e) {
    if (!usePromiseToast && !silentErrors) toast.error(e?.message || 'An error occurred', { position: 'bottom-right', autoClose: 4000 });
    throw e;
  } finally {
    dispatch(doneLoading());
  }
};

const authHeader = () => 'Bearer ' + (localStorage.getItem('authToken')?.toString() || '');

export const get = async (path, queryParams = {}, baseUrl = BASE_URL) => {
  dispatch(startLoading());
  try {
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${baseUrl}${path}${queryString ? `?${queryString}` : ''}`;
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', Authorization: authHeader() },
      method: 'GET',
    });
    const data = await res.json();
    if (!data?.status) toast.error(data?.message || 'An error occurred', { position: 'bottom-right', autoClose: 2000 });
    return data;
  } finally {
    dispatch(doneLoading());
  }
};

export const post = async (path, body, baseUrl = BASE_URL, usePromiseToast = true, stopAutoClose = false) => {

  const isAuthPath = path === 'auth';
  const skipToast = isAuthPath ? false : usePromiseToast;
  return await runRequest(
    () =>
      fetch(`${baseUrl}${path}`, {
        headers: { 'Content-Type': 'application/json', Authorization: authHeader() },
        body: JSON.stringify(body),
        method: 'POST',
      }),
    { usePromiseToast: skipToast, pendingText: 'Saving...', stopAutoClose, silentErrors: isAuthPath }
  );
};

export const put = async (path, body, baseUrl = BASE_URL, usePromiseToast = true) => {
  return await runRequest(
    () =>
      fetch(`${baseUrl}${path}`, {
        headers: { 'Content-Type': 'application/json', Authorization: authHeader() },
        body: JSON.stringify(body),
        method: 'PUT',
      }),
    { usePromiseToast, pendingText: 'Updating...' }
  );
};

export const deleteMethod = async (path, body, baseUrl = BASE_URL, usePromiseToast = true) => {
  return await runRequest(
    () =>
      fetch(`${baseUrl}${path}`, {
        headers: { 'Content-Type': 'application/json', Authorization: authHeader() },
        body: JSON.stringify(body),
        method: 'DELETE',
      }),
    { usePromiseToast, pendingText: 'Deleting...' }
  );
};

export const imageUpload = async (file, folderName, successCallback, errorCallback, baseUrl = BASE_URL) => {
  dispatch(startLoading());
  const originalFileName = file.name;
  try {
    const compressed = await new Promise((resolve, reject) => {
      new Compressor(file, { quality: 0.5, success: resolve, error: reject });
    });
    let compressedFile = compressed;
    let size = compressedFile.size;
    while (size > 1048576) {
      const nextQuality = Math.max(0.2, 1048576 / size);
      // eslint-disable-next-line no-await-in-loop
      compressedFile = await new Promise((resolve) => {
        new Compressor(compressedFile, { quality: nextQuality, success: resolve });
      });
      size = compressedFile.size;
    }
    const formData = new FormData();
    formData.append('file', compressedFile, originalFileName);
    formData.append('folderName', folderName);
    formData.append('tag', `WEB_${ENV}`);
    const exec = async () => {
      const res = await fetch(`${baseUrl}common/imageUpload`, { method: 'POST', body: formData, headers: { Authorization: authHeader() } });
      const data = await res.json();
      if (!data?.status) throw data;
      return data;
    };
    const data = await withToast(exec(), { pending: 'Uploading image...' });
    successCallback(data);
    return data;
  } catch (e) {
    toast.error(e?.message || 'Image Upload Failed', { position: 'bottom-right', autoClose: 2000 });
    errorCallback('Upload Failed.');
    throw e;
  } finally {
    dispatch(doneLoading());
  }
};

export const fileUpload = async (file, folderName, successCallback, errorCallback, baseUrl = BASE_URL) => {
  dispatch(startLoading());
  try {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('folderName', folderName);
    formData.append('tag', `WEB_${ENV}`);
    const exec = async () => {
      const res = await fetch(`${baseUrl}common/imageUpload`, {
        method: 'POST',
        body: formData,
        headers: { Authorization: authHeader() },
      });
      const data = await res.json();
      if (!data?.status) throw data;
      return data;
    };
    const data = await withToast(exec(), { pending: 'Uploading file...' });
    successCallback(data);
    return data;
  } catch (e) {
    toast.error(e?.message || 'File Upload Failed', { position: 'bottom-right', autoClose: 2000 });
    errorCallback('Upload Failed.');
    throw e;
  } finally {
    dispatch(doneLoading());
  }
};

export const imageUrlUpload = async (url, publicId, name, folderName, formats, successCallback, errorCallback, baseUrl = BASE_URL) => {
  dispatch(startLoading());
  try {
    const formData = new FormData();
    formData.append('url', url);
    formData.append('publicId', publicId);
    formData.append('name', name);
    formData.append('folderName', folderName);
    formData.append('formats', formats);
    if (ENV === 'BETA') formData.append('tag', 'DELETABLE');
    const exec = async () => {
      const res = await fetch(`${baseUrl}common/imageUpload`, { method: 'POST', body: formData, headers: { Authorization: authHeader() } });
      const data = await res.json();
      if (!data?.status) throw data;
      return data;
    };
    const data = await withToast(exec(), { pending: 'Uploading...' });
    successCallback(data);
    return data;
  } catch (e) {
    toast.error(e?.message || 'Upload Failed', { position: 'bottom-right', autoClose: 2000 });
    errorCallback('Upload Failed.');
    throw e;
  } finally {
    dispatch(doneLoading());
  }
};
